import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../services/AuthContext';

const CoachSidebar = ({ activeTab = 'Insights', onTabChange, onOpenAvatarModal, onOpenPromptModal, onOpenSettingsModal }) => {
  const { user } = useAuth();
  return (
    <aside className="w-full lg:w-80 flex flex-col shrink-0 space-y-6">
      <div className="bg-[var(--surface-white)] rounded-3xl shadow-xl shadow-black/5 border border-[var(--border-light)] overflow-hidden">


        <div className="p-6 space-y-8">
          <section className="space-y-4">
            <h3 className="text-xs font-bold text-[var(--text-secondary)]/60 uppercase tracking-widest flex items-center gap-2">
              <span className="material-symbols-outlined text-base">face</span>
              AI Persona
            </h3>
            <div className="p-8 rounded-[2rem] border border-[var(--border-light)] bg-gradient-to-b from-[var(--surface-white)] to-[var(--off-white)]/50 text-center relative group shadow-inner">
              <div className="w-28 h-28 mx-auto rounded-full overflow-hidden border-4 border-[var(--surface-white)] shadow-xl mb-6 relative ring-4 ring-[var(--off-white)]/50 bg-[var(--off-white)] flex items-center justify-center">
                {user?.avatarUrl ? (
                  <img alt="AI Avatar" className="w-full h-full object-cover transform transition-transform group-hover:scale-110 duration-500" src={`${process.env.REACT_APP_API_URL}${user.avatarUrl}`} />
                ) : (
                  <span className="material-symbols-outlined text-4xl text-gray-200">person</span>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
              <h4 className="font-black text-[var(--text-primary)] text-xl tracking-tight mb-1">{user?.name || 'Avatar"'}</h4>
              <p className="text-sm font-bold text-[var(--tangerine)] mb-6 uppercase tracking-widest leading-tight">{user?.specialization || 'Coaching Specialist'}</p>
              <button
                onClick={onOpenAvatarModal}
                className="text-xs text-white bg-[var(--tangerine)] hover:bg-[#d97700] px-6 py-3 rounded-2xl font-bold shadow-lg shadow-orange-500/30 transition-all hover:-translate-y-1 w-full flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-sm">edit</span>
                Edit Avatar
              </button>
            </div>

            {/* Tone of Voice commented out
            <div className="pt-2">
              <label className="block text-xs font-bold text-gray-700 mb-4">Tone of Voice</label>
              <div className="px-2">
                <div className="relative flex flex-col gap-4">
                  <input 
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--tangerine)]" 
                    max="5" min="1" step="1" 
                    style={{backgroundImage: 'linear-gradient(to right, var(--tangerine), var(--tangerine))', backgroundRepeat: 'no-repeat', backgroundSize: '50% 100%'}} 
                    type="range" 
                    defaultValue="3" 
                  />
                  <div className="flex justify-between w-full px-0.5">
                    <div className="flex flex-col items-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-300 mb-2"></div>
                      <span className="text-[10px] font-bold text-[var(--text-secondary)]/60">Warm</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-300 mb-2"></div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-300 mb-2"></div>
                      <span className="text-[10px] font-bold text-[var(--text-primary)]">Balanced</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-300 mb-2"></div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-300 mb-2"></div>
                      <span className="text-[10px] font-bold text-[var(--text-secondary)]/60">Formal</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            */}
          </section>

          <section className="pt-6 border-t border-[var(--border-light)]">
            <h3 className="text-xs font-bold text-[var(--text-secondary)]/60 uppercase tracking-widest mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-base">school</span>
              Coaching Philosophy
            </h3>
            <div className="bg-[var(--off-white)] border border-[var(--border-light)] rounded-2xl p-4 mb-4 relative group">
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs font-bold text-[var(--text-primary)]">Base Knowledge</span>
                <button
                  onClick={onOpenPromptModal}
                  className="text-[10px] text-gray-500 bg-[var(--surface-white)] border border-[var(--border-light)] rounded px-2 py-0.5 hover:text-[var(--tangerine)] hover:border-[var(--tangerine)] transition-colors"
                >
                  Edit Prompt
                </button>
              </div>
              <p className="text-xs text-[var(--text-secondary)] line-clamp-3 leading-relaxed mt-2 italic">
                {user?.coachSettings?.config?.system_prompt || "Define your coaching philosophy to guide the AI..." }
              </p>
            </div>
            {/* Active Instruction commented out
            <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4">
              <h5 className="text-xs font-bold text-orange-800 mb-2 flex items-center gap-1">
                <span className="material-symbols-outlined text-base">tips_and_updates</span> Active Instruction
              </h5>
              <p className="text-xs font-medium text-orange-700 leading-snug">
                "Always ask for one specific action item at the end of every session."
              </p>
            </div>
            */}
          </section>

          <section className="pt-6 border-t border-[var(--border-light)]">
            <Link
              to="/coach/settings"
              className="w-full py-3 bg-[var(--surface-white)] border border-[var(--border-light)] rounded-xl text-sm font-bold text-[var(--text-secondary)] hover:bg-[var(--off-white)] hover:text-[var(--tangerine)] hover:border-[var(--tangerine)] transition-colors flex items-center justify-center gap-2 shadow-sm"
            >
              <span className="material-symbols-outlined text-lg">settings</span>
              Advanced Settings
            </Link>
          </section>
        </div>
      </div>
    </aside>
  );
};

export default CoachSidebar;
